/**
 * Hooks para el módulo Pagos — Fase 5
 *
 * Todas las operaciones van directo a Supabase (decisión #014).
 * Las RLS (decisión #023) garantizan que solo admin_general / operador
 * pueden insertar/actualizar; la empresa solo consulta sus pagos.
 *
 * El cierre del cupón al acreditar pagos lo maneja un trigger SQL
 * (`tg_pagos_after_change_update_cupon`), no este hook.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { QK_CUPONES } from './useCupones';
import type {
  Pago,
  PagoEstado,
  PagoRegistrarPayload,
  PagoConfirmarPayload,
  PagoRechazarPayload,
  PagoInformarEmpresaPayload,
} from '../types/pago';

// ── Query Keys ────────────────────────────────────────────────────────────────

export const QK_PAGOS = {
  all: ['pagos'] as const,
  list: (filtros: PagosFiltros = {}) =>
    [
      'pagos',
      'list',
      filtros.empresaId ?? 'all',
      filtros.periodoId ?? 'all',
      filtros.estado ?? 'all',
    ] as const,
  byCupon: (cuponId: string | undefined) => ['pagos', 'cupon', cuponId] as const,
  detail:  (pagoId: string | undefined)  => ['pagos', 'detail', pagoId] as const,
};

// ── Joins estándar ────────────────────────────────────────────────────────────

const PAGO_SELECT = '*, empresa:empresas(id, razon_social, cuit), periodo:periodos(id, periodo, descripcion), cupon:cupones(id, codigo, monto_total, estado)';

// ── Filtros ───────────────────────────────────────────────────────────────────

export interface PagosFiltros {
  empresaId?: string;
  periodoId?: string;
  estado?: PagoEstado;
}

// ── LIST ──────────────────────────────────────────────────────────────────────

/** Lista de pagos con filtros opcionales. Ordenada por created_at desc. */
export function usePagos(filtros: PagosFiltros = {}) {
  return useQuery({
    queryKey: QK_PAGOS.list(filtros),
    queryFn: async () => {
      let query = supabase
        .from('pagos')
        .select(PAGO_SELECT)
        .order('created_at', { ascending: false });

      if (filtros.empresaId) query = query.eq('empresa_id', filtros.empresaId);
      if (filtros.periodoId) query = query.eq('periodo_id', filtros.periodoId);
      if (filtros.estado)    query = query.eq('estado', filtros.estado);

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as Pago[];
    },
  });
}

// ── BY CUPON ──────────────────────────────────────────────────────────────────

/** Pagos asociados a un cupón puntual (para CuponDetalle). */
export function usePagosPorCupon(cuponId: string | undefined) {
  return useQuery({
    queryKey: QK_PAGOS.byCupon(cuponId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pagos')
        .select(PAGO_SELECT)
        .eq('cupon_id', cuponId!)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as Pago[];
    },
    enabled: !!cuponId,
  });
}

// ── SINGLE ────────────────────────────────────────────────────────────────────

export function usePago(pagoId: string | undefined) {
  return useQuery({
    queryKey: QK_PAGOS.detail(pagoId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pagos')
        .select(PAGO_SELECT)
        .eq('id', pagoId!)
        .single();
      if (error) throw error;
      return data as Pago;
    },
    enabled: !!pagoId,
  });
}

// ── REGISTRAR ─────────────────────────────────────────────────────────────────

/**
 * Registra un pago manual (siempre estado='pendiente').
 * El comprobante (si va a haber uno) debe haberse subido antes vía
 * `useUploadComprobante` y pasarse en `comprobante_url`.
 */
export function useRegistrarPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: PagoRegistrarPayload) => {
      const { data, error } = await supabase
        .from('pagos')
        .insert({ ...payload, estado: 'pendiente' as PagoEstado })
        .select('*')
        .single();
      if (error) throw error;
      return data as Pago;
    },
    onSuccess: (pago) => {
      qc.invalidateQueries({ queryKey: QK_PAGOS.all });
      if (pago.cupon_id) {
        qc.invalidateQueries({ queryKey: QK_PAGOS.byCupon(pago.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.detail(pago.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.all });
      }
    },
  });
}

// ── INFORMAR PAGO (rol empresa) ───────────────────────────────────────────────

/**
 * La empresa informa una transferencia: sube el comprobante a su carpeta del
 * bucket y llama al RPC `informar_pago_empresa`, que crea un pago PENDIENTE
 * (ADR #043). La acreditación sigue siendo exclusiva de ATSA.
 */
export function useInformarPagoEmpresa() {
  const qc = useQueryClient();
  const upload = useUploadComprobante();
  return useMutation({
    mutationFn: async (p: PagoInformarEmpresaPayload) => {
      const { path } = await upload.mutateAsync({
        empresaId: p.empresa_id,
        scope: p.cupon_id,
        file: p.file,
      });
      const { data, error } = await supabase.rpc('informar_pago_empresa', {
        p_cupon_id: p.cupon_id,
        p_monto_pagado: p.monto_pagado,
        p_fecha_pago: p.fecha_pago,
        p_nro_comprobante: p.nro_comprobante,
        p_medio_pago: p.medio_pago,
        p_comprobante_path: path,
        p_observaciones: p.observaciones ?? null,
      });
      if (error) throw error;
      return data as string;
    },
    onSuccess: (_id, p) => {
      qc.invalidateQueries({ queryKey: QK_PAGOS.all });
      qc.invalidateQueries({ queryKey: QK_PAGOS.byCupon(p.cupon_id) });
      qc.invalidateQueries({ queryKey: QK_CUPONES.detail(p.cupon_id) });
    },
  });
}

// ── CONFIRMAR (acreditar) ─────────────────────────────────────────────────────

/**
 * Marca un pago como 'acreditado'.
 * El trigger SQL recalcula el estado del cupón asociado.
 */
export function useConfirmarPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId }: PagoConfirmarPayload) => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('pagos')
        .update({
          estado: 'acreditado' as PagoEstado,
          confirmado_por:     userId,
          fecha_confirmacion: now,
          fecha_acreditacion: now,
        })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Pago;
    },
    onSuccess: (pago) => {
      qc.invalidateQueries({ queryKey: QK_PAGOS.all });
      if (pago.cupon_id) {
        qc.invalidateQueries({ queryKey: QK_PAGOS.byCupon(pago.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.detail(pago.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.all });
      }
    },
  });
}

// ── RECHAZAR ──────────────────────────────────────────────────────────────────

export function useRechazarPago() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, userId, motivo }: PagoRechazarPayload) => {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('pagos')
        .update({
          estado: 'rechazado' as PagoEstado,
          rechazado_por:  userId,
          fecha_rechazo:  now,
          motivo_rechazo: motivo,
        })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data as Pago;
    },
    onSuccess: (pago) => {
      qc.invalidateQueries({ queryKey: QK_PAGOS.all });
      if (pago.cupon_id) {
        qc.invalidateQueries({ queryKey: QK_PAGOS.byCupon(pago.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.detail(pago.cupon_id) });
        qc.invalidateQueries({ queryKey: QK_CUPONES.all });
      }
    },
  });
}

// ── UPLOAD COMPROBANTE ────────────────────────────────────────────────────────

export interface UploadComprobanteArgs {
  empresaId: string;
  /** Identificador estable para el path (típicamente el cuponId o un uuid). */
  scope: string;
  file: File;
}

export interface UploadComprobanteResult {
  /** Path interno en el bucket (para guardar en pagos.comprobante_url). */
  path: string;
  /** URL firmada de corta duración para previsualizar (1 hora). */
  signedUrl: string;
}

/**
 * Sube un comprobante al bucket privado `comprobantes`.
 * Convención de path: `{empresa_id}/{scope}_{timestamp}_{filename}`.
 */
export function useUploadComprobante() {
  return useMutation({
    mutationFn: async ({
      empresaId,
      scope,
      file,
    }: UploadComprobanteArgs): Promise<UploadComprobanteResult> => {
      const safeName = file.name.replace(/[^\w.-]+/g, '_');
      const path = `${empresaId}/${scope}_${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage
        .from('comprobantes')
        .upload(path, file, {
          contentType: file.type,
          upsert: false,
        });
      if (upErr) throw upErr;

      const { data: signed, error: signErr } = await supabase.storage
        .from('comprobantes')
        .createSignedUrl(path, 3600);
      if (signErr) throw signErr;

      return { path, signedUrl: signed?.signedUrl ?? '' };
    },
  });
}

/** Obtiene una URL firmada para un path ya guardado (visualización posterior). */
export function useComprobanteUrl(path: string | null | undefined, expiresIn = 3600) {
  return useQuery({
    queryKey: ['comprobante-signed', path],
    queryFn: async () => {
      const { data, error } = await supabase.storage
        .from('comprobantes')
        .createSignedUrl(path!, expiresIn);
      if (error) throw error;
      return data?.signedUrl ?? null;
    },
    enabled: !!path,
    staleTime: (expiresIn - 60) * 1000, // refresca antes de expirar
  });
}
